import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const features = [
  'CBSE & NEB affiliated curriculum',
  'Expert and experienced faculty',
  'State-of-the-art infrastructure',
  'Focus on holistic development',
  'Strong alumni network',
  'Inclusive learning environment',
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const AboutSection = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();
  const { settings } = useSiteSettings();
  const campusImageUrl = settings?.campusImage?.url || '';

  return (
    <section className="py-14 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* Left: Text */}
          <motion.div ref={leftRef} initial={{ opacity: 0, x: -40 }} animate={leftVisible ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }}>
            <SectionLabel text={`About ${settings?.schoolName || "Us"}`} />
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
              Shaping Future <span className="text-[#2563EB]">Leaders</span> Since 1999
            </h2>
            <div className="w-12 h-1 bg-[#2563EB] rounded-full mb-8" />
            <p className="text-slate-500 text-lg leading-relaxed mb-5">
              {settings?.schoolName || "Early Boarding School & Montessori"} is a leading educational institution
              in Kathmandu, Nepal, dedicated to providing world-class education that nurtures
              intellectual curiosity, creativity, and character.
            </p>
            <p className="text-slate-500 text-lg leading-relaxed mb-10">
              With a legacy of over 25 years, we have produced thousands of successful alumni
              who are making meaningful contributions across various fields globally.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-12">
              {features.map((feature, idx) => (
                <motion.div key={idx}
                  initial={{ opacity: 0, x: -20 }} animate={leftVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="flex items-start gap-3 py-3 border-t border-gray-100">
                  <FiCheckCircle className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                  <span className="text-[#0F172A] font-medium text-sm">{feature}</span>
                </motion.div>
              ))}
            </div>

            <Link to="/about"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2563EB] text-white font-bold uppercase tracking-wide text-sm hover:bg-[#1D4ED8] transition-all rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5">
              Discover More <FiArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Right: Image */}
          <motion.div ref={rightRef} initial={{ opacity: 0, x: 40 }} animate={rightVisible ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }} className="relative w-full">
            <div className="relative rounded-3xl border-4 border-[#2563EB]/15 shadow-xl bg-white p-3">
              <div className="w-full h-[320px] sm:h-[420px] lg:h-[480px] rounded-2xl bg-blue-50 relative overflow-hidden">
                {campusImageUrl ? (
                  <img src={campusImageUrl} alt="School Campus" className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1e3a8a] text-white p-8">
                    <div className="w-16 h-16 border-2 border-sky-400 rounded-xl flex items-center justify-center mb-6">
                      <span className="font-mono text-sky-400">IMG</span>
                    </div>
                    <h3 className="text-2xl font-bold tracking-widest mb-2 text-center">{settings?.schoolName || "School Campus"}</h3>
                  </div>
                )}

                {/* 25+ badge — inside image, always visible */}
                <div className="absolute bottom-4 left-4 bg-[#2563EB] text-white px-5 py-4 rounded-2xl shadow-xl border-2 border-white/30 backdrop-blur-sm">
                  <div className="text-3xl sm:text-4xl font-black font-mono leading-none mb-1">25+</div>
                  <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-blue-200 leading-tight">Years of<br/>Excellence</div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;
